import { type ApiFetcherArgs, initClient, tsRestFetchApi } from "@ts-rest/core";
import * as Sentry from "@sentry/react";

import { auth } from "~/lib/firebase";
import { getAuthToken } from "~/providers/store/auth";
import { MINUTE_MS } from "~/utils/constants";
import { KANPLA_API_URL } from "~/utils/env";

import { apiContract } from "./contract";

// Above the Adyen sync wait (150 s), so a request holding for the terminal is never cut short.
export const REQUEST_TIMEOUT_MS = 5 * MINUTE_MS;

export const client = initClient(apiContract, {
  baseUrl: KANPLA_API_URL,
  // Placeholder — the real `authorization` is injected dynamically
  baseHeaders: {
    authorization: "Bearer token"
  },
  jsonQuery: true,
  api: fetchWithAuthRetry
});

/**
 * Send a request with the current Firebase ID token, retrying once on 401 with a forced refresh.
 *
 * ADR-0013: the SDK can hand back a stale token it still considers valid (missed refresh, clock
 * drift), so force one refresh and retry before the 401 handler logs the till out.
 */
export async function fetchWithAuthRetry({ headers, ...args }: ApiFetcherArgs) {
  await auth.authStateReady();

  const request = (idToken: string | undefined) => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort(
        new DOMException(
          `Request timed out after ${REQUEST_TIMEOUT_MS}ms`,
          "TimeoutError"
        )
      );
    }, REQUEST_TIMEOUT_MS);

    return tsRestFetchApi({
      ...args,
      fetchOptions: { ...args.fetchOptions, signal: controller.signal },
      headers: {
        ...headers,
        ...getAuthHeader(),
        ...(idToken ? { "x-id-token": idToken } : {})
      }
    }).finally(() => {
      clearTimeout(timer);
    });
  };

  const response = await request(await auth.currentUser?.getIdToken());
  if (response.status !== 401) return response;

  const refreshed = await forceRefreshIdToken();

  return refreshed ? request(refreshed) : response;
}

// Undefined when there is no Firebase user or the refresh was rejected, so the caller keeps its
// original 401 and the usual logout path runs.
async function forceRefreshIdToken(): Promise<string | undefined> {
  try {
    return await auth.currentUser?.getIdToken(true);
  } catch {
    return undefined;
  }
}

/**
 * Build the legacy auth + Sentry trace headers for an outgoing request.
 * TO-DO: [PWEB-99] Drop the token
 */
function getAuthHeader(): Record<string, string> {
  const activeSpan = Sentry.getActiveSpan();

  const rootSpan: Sentry.Span | undefined = activeSpan
    ? Sentry.getRootSpan(activeSpan)
    : undefined;

  const sentryTraceHeader: string | undefined = rootSpan
    ? Sentry.spanToTraceHeader(rootSpan)
    : undefined;

  const sentryBaggageHeader: string | undefined = rootSpan
    ? Sentry.spanToBaggageHeader(rootSpan)
    : undefined;

  const token = getAuthToken();

  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "true"
  };

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  if (sentryTraceHeader) {
    headers["sentry-trace"] = sentryTraceHeader;
  }

  if (sentryBaggageHeader) {
    headers.baggage = sentryBaggageHeader;
  }

  return headers;
}
